import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { dashboardAPI, platformDataAPI, campaignsAPI, advertisersAPI } from '../lib/api';
import { formatNumber, formatCurrency, formatPercentage, getPlatformColor } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { usePlatformAccess } from '../hooks/usePlatformAccess';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Calendar as CalendarIcon, Download, Loader2, Filter, ChevronDown, X, Globe, Server, FileText, FileSpreadsheet, Image, Building2 } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const Reports = () => {
  const { user, isAdvertiser, isAdmin, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [campaignPagination, setCampaignPagination] = useState({ page: 1, page_size: 20, total_count: 0, total_pages: 0 });
  const [campaignPage, setCampaignPage] = useState(1);
  
  // Refs for chart capture
  const pieChartRef = useRef(null);
  const lineChartRef = useRef(null);
  
  // DV360 Source/Domain Report
  const [dv360Report, setDv360Report] = useState({ sources: [], domains: [] });
  const [dv360Loading, setDv360Loading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'dv360-breakdown'
  
  // Multi-select filters
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);
  const [platformDropdownOpen, setPlatformDropdownOpen] = useState(false);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const [adAccountDropdownOpen, setAdAccountDropdownOpen] = useState(false);
  const [platformSearch, setPlatformSearch] = useState('');
  const [adAccountSearch, setAdAccountSearch] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [allAdAccounts, setAllAdAccounts] = useState([]); // All ad accounts (unfiltered)
  const [adAccounts, setAdAccounts] = useState([]); // Filtered by selected platforms
  
  // Advertiser filter for advertiser users
  const isAdvertiserUser = isAdvertiser();
  const isAdvertiserRole = user?.role === 'advertiser';
  const advertiserIdFilter = isAdvertiserUser ? user?.advertiser_id : null;
  const canViewSourceDomain = isSuperAdmin() || isAdmin();  // Only admins can see Source/Domain tabs
  
  // Use platform access hook to get filtered platforms
  const { effectivePlatforms, loading: platformAccessLoading } = usePlatformAccess(advertiserIdFilter);
  
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [activePreset, setActivePreset] = useState('last30');

  // Date range presets
  const datePresets = [
    { key: 'today', label: 'Today', getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
    { key: 'yesterday', label: 'Yesterday', getRange: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) }) },
    { key: 'last7', label: 'Last 7 Days', getRange: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
    { key: 'last30', label: 'Last 30 Days', getRange: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
    { key: 'last90', label: 'Last 90 Days', getRange: () => ({ from: subDays(new Date(), 90), to: new Date() }) },
    { key: 'thisMonth', label: 'This Month', getRange: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
    { key: 'lastMonth', label: 'Last Month', getRange: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) },
  ];

  const applyDatePreset = (preset) => {
    const range = preset.getRange();
    setDateRange(range);
    setActivePreset(preset.key);
  };

  const handleCustomDateChange = (range) => {
    if (range?.from) {
      // Allow single day selection - if only 'from' is set, use it as both from and to
      const newRange = {
        from: range.from,
        to: range.to || range.from  // If no 'to' date, use 'from' date (single day)
      };
      setDateRange(newRange);
      setActivePreset(null); // Clear preset selection when custom date is chosen
    }
  };

  const fetchFilters = async () => {
    try {
      // Build params to filter campaigns by advertiser
      const campaignParams = {};
      if (advertiserIdFilter) {
        campaignParams.advertiser_id = advertiserIdFilter;
      }
      
      const campaignsRes = await campaignsAPI.getFiltered(campaignParams);
      
      // Use effectivePlatforms from hook (already filtered by advertiser restrictions)
      setPlatforms(effectivePlatforms);
      setCampaigns(campaignsRes.data);
      
      // Extract unique ad accounts from campaigns (with platform info)
      const adAccountMap = new Map();
      (campaignsRes.data || []).forEach(c => {
        if (c.ad_account_id && c.ad_account_name) {
          const existing = adAccountMap.get(c.ad_account_id);
          if (existing) {
            // Add platforms to existing ad account
            const newPlatforms = new Set([...existing.platforms, ...(c.platforms || [])]);
            existing.platforms = [...newPlatforms];
          } else {
            adAccountMap.set(c.ad_account_id, { 
              id: c.ad_account_id, 
              name: c.ad_account_name,
              platforms: c.platforms || []
            });
          }
        }
      });
      const uniqueAdAccounts = Array.from(adAccountMap.values());
      setAllAdAccounts(uniqueAdAccounts);
      setAdAccounts(uniqueAdAccounts);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };
  
  // Cascading filter: When platforms change, filter ad accounts
  useEffect(() => {
    if (selectedPlatforms.length === 0) {
      // Show all ad accounts when no platform is selected
      setAdAccounts(allAdAccounts);
    } else {
      // Filter ad accounts to only those with campaigns on selected platforms
      const filtered = allAdAccounts.filter(acc => 
        acc.platforms?.some(p => selectedPlatforms.includes(p))
      );
      setAdAccounts(filtered);
      // Clear selected ad accounts that are no longer valid
      setSelectedAdAccounts(prev => prev.filter(id => filtered.some(acc => acc.id === id)));
    }
  }, [selectedPlatforms, allAdAccounts]);
  
  // Get filtered campaigns based on selected platforms and ad accounts
  const getFilteredCampaigns = () => {
    let filtered = campaigns;
    
    // Filter by selected platforms
    if (selectedPlatforms.length > 0) {
      filtered = filtered.filter(c => 
        c.platforms?.some(p => selectedPlatforms.includes(p))
      );
    }
    
    // Filter by selected ad accounts
    if (selectedAdAccounts.length > 0) {
      filtered = filtered.filter(c => selectedAdAccounts.includes(c.ad_account_id));
    }
    
    return filtered;
  };
  
  const filteredCampaigns = getFilteredCampaigns();
  
  // Re-fetch when effectivePlatforms changes
  useEffect(() => {
    if (!platformAccessLoading) {
      fetchFilters();
    }
  }, [effectivePlatforms, platformAccessLoading, advertiserIdFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
      };

      // Add advertiser filter for advertiser users
      if (advertiserIdFilter) {
        params.advertiser_id = advertiserIdFilter;
      }

      // Add platform filter - use selected platforms or effectivePlatforms for restricted users
      if (selectedPlatforms.length > 0) {
        params.platforms = selectedPlatforms.join(',');
      } else if (effectivePlatforms.length > 0 && effectivePlatforms.length < 8) {
        // Auto-filter by user's allowed platforms if they have restrictions
        params.platforms = effectivePlatforms.join(',');
      }

      // Add ad_account filter
      if (selectedAdAccounts.length > 0) {
        params.ad_account_ids = selectedAdAccounts.join(',');
      }

      // Add campaign_ids filter if campaigns are selected
      if (selectedCampaigns.length > 0) {
        params.campaign_ids = selectedCampaigns.join(',');
      }

      const [dataRes, platformSummaryRes, campaignSummaryRes] = await Promise.all([
        dashboardAPI.getTrend(params),
        dashboardAPI.getPlatformSummary(params),
        dashboardAPI.getCampaignSummary({ ...params, page: campaignPage, page_size: 20 }),
      ]);

      let filteredPlatformData = platformSummaryRes.data;
      let filteredTrendData = dataRes.data;

      // Additional client-side filter by effectivePlatforms for restricted users
      if (effectivePlatforms.length > 0 && effectivePlatforms.length < 8) {
        filteredPlatformData = filteredPlatformData.filter(p => effectivePlatforms.includes(p.platform));
      } else if (selectedPlatforms.length > 0) {
        filteredPlatformData = filteredPlatformData.filter(p => selectedPlatforms.includes(p.platform));
      }

      setPlatformData(filteredPlatformData);
      setTrendData(filteredTrendData);
      setCampaignData(campaignSummaryRes.data.campaigns || []);
      setCampaignPagination(campaignSummaryRes.data.pagination || { page: 1, page_size: 20, total_count: 0, total_pages: 0 });
    } catch (error) {
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDV360Report = async () => {
    setDv360Loading(true);
    try {
      const params = {
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
      };
      const response = await dashboardAPI.getDV360SourceDomain(params);
      setDv360Report(response.data);
    } catch (error) {
      console.error('DV360 report error:', error);
      toast.error('Failed to fetch DV360 breakdown. Make sure DV360 is connected.');
    } finally {
      setDv360Loading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Fetch DV360 report when switching to that tab
    if (activeTab === 'dv360-breakdown') {
      fetchDV360Report();
    }
  }, [dateRange, selectedPlatforms, selectedAdAccounts, selectedCampaigns, activeTab, campaignPage, effectivePlatforms]);

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => {
      const newPlatforms = prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform];
      return newPlatforms;
    });
    // Clear downstream selections when platform changes
    setSelectedAdAccounts([]);
    setSelectedCampaigns([]);
  };

  const toggleCampaign = (campaignId) => {
    setSelectedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(c => c !== campaignId)
        : [...prev, campaignId]
    );
  };

  const clearPlatformFilter = () => {
    setSelectedPlatforms([]);
    setSelectedAdAccounts([]);
    setSelectedCampaigns([]);
  };

  const clearCampaignFilter = () => {
    setSelectedCampaigns([]);
  };

  const toggleAdAccount = (adAccountId) => {
    setSelectedAdAccounts(prev => {
      const newAccounts = prev.includes(adAccountId)
        ? prev.filter(a => a !== adAccountId)
        : [...prev, adAccountId];
      return newAccounts;
    });
    // Clear campaign selection when ad account changes
    setSelectedCampaigns([]);
  };

  const clearAdAccountFilter = () => {
    setSelectedAdAccounts([]);
    setSelectedCampaigns([]);
  };

  const CHART_COLORS = ['#0F172A', '#059669', '#0284C7', '#D97706', '#7C3AED', '#DB2777', '#06B6D4', '#84CC16', '#F43F5E'];

  // Export Platform Report as CSV
  const handleExportPlatformCSV = () => {
    let csv = '';
    csv += `Report PRO - Platform Performance Report\n`;
    csv += `Date Range: ${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}\n`;
    csv += `Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}\n`;
    csv += `\n`;
    
    const totalImpressions = platformData.reduce((sum, p) => sum + (p.impressions || 0), 0);
    const totalClicks = platformData.reduce((sum, p) => sum + (p.clicks || 0), 0);
    const totalSpend = platformData.reduce((sum, p) => sum + (p.spend || 0), 0);
    const totalConversions = platformData.reduce((sum, p) => sum + (p.conversions || 0), 0);
    const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;
    
    csv += `SUMMARY\n`;
    csv += `Total Impressions,${totalImpressions}\n`;
    csv += `Total Clicks,${totalClicks}\n`;
    csv += `Overall CTR,${avgCTR}%\n`;
    csv += `Total Spend,$${totalSpend.toFixed(2)}\n`;
    csv += `Total Conversions,${totalConversions}\n`;
    csv += `\n`;
    
    csv += `PLATFORM BREAKDOWN\n`;
    csv += `Platform,Impressions,Clicks,CTR,Spend,CPC,CPM,Conversions\n`;
    platformData.forEach(p => {
      csv += `${p.platform},${p.impressions},${p.clicks},${p.ctr}%,$${(p.spend || 0).toFixed(2)},$${(p.cpc || 0).toFixed(2)},$${(p.cpm || 0).toFixed(2)},${p.conversions || 0}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Platform_Report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Platform report exported successfully');
  };

  // Export Date-wise Report as CSV
  const handleExportDatewiseCSV = () => {
    let csv = '';
    csv += `Report PRO - Comprehensive Performance Report\n`;
    csv += `Date Range: ${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}\n`;
    csv += `Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}\n`;
    csv += `\n`;
    
    // Platform Breakdown Section
    csv += `PLATFORM BREAKDOWN\n`;
    csv += `Platform,Impressions,Clicks,CTR,Spend,CPC,CPM,Conversions\n`;
    platformData.forEach(p => {
      csv += `${p.platform},${p.impressions},${p.clicks},${p.ctr}%,$${(p.spend || 0).toFixed(2)},$${(p.cpc || 0).toFixed(2)},$${(p.cpm || 0).toFixed(2)},${p.conversions || 0}\n`;
    });
    if (platformData.length > 0) {
      const totals = platformData.reduce((acc, p) => ({
        impressions: acc.impressions + p.impressions,
        clicks: acc.clicks + p.clicks,
        spend: acc.spend + (p.spend || 0),
        conversions: acc.conversions + (p.conversions || 0)
      }), { impressions: 0, clicks: 0, spend: 0, conversions: 0 });
      const totalCTR = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : 0;
      const totalCPC = totals.clicks > 0 ? (totals.spend / totals.clicks).toFixed(2) : 0;
      const totalCPM = totals.impressions > 0 ? ((totals.spend / totals.impressions) * 1000).toFixed(2) : 0;
      csv += `TOTAL,${totals.impressions},${totals.clicks},${totalCTR}%,$${totals.spend.toFixed(2)},$${totalCPC},$${totalCPM},${totals.conversions}\n`;
    }
    csv += `\n`;
    
    // Campaign Breakdown Section
    csv += `CAMPAIGN BREAKDOWN\n`;
    csv += `Campaign,Platform,Ad Account,Impressions,Clicks,CTR,Spend,CPC,CPM,Conversions\n`;
    campaignData.forEach(c => {
      const campaignName = (c.campaign_name || 'Unknown').replace(/,/g, ' ');
      const adAccountName = (c.ad_account_name || '').replace(/,/g, ' ');
      csv += `"${campaignName}",${c.platform},"${adAccountName}",${c.impressions},${c.clicks},${c.ctr}%,$${(c.spend || 0).toFixed(2)},$${(c.cpc || 0).toFixed(2)},$${(c.cpm || 0).toFixed(2)},${c.conversions || 0}\n`;
    });
    if (campaignData.length > 0) {
      const totals = campaignData.reduce((acc, c) => ({
        impressions: acc.impressions + c.impressions,
        clicks: acc.clicks + c.clicks,
        spend: acc.spend + (c.spend || 0),
        conversions: acc.conversions + (c.conversions || 0)
      }), { impressions: 0, clicks: 0, spend: 0, conversions: 0 });
      const totalCTR = totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : 0;
      csv += `TOTAL (${campaignData.length} campaigns),,,${totals.impressions},${totals.clicks},${totalCTR}%,$${totals.spend.toFixed(2)},,,,${totals.conversions}\n`;
    }
    csv += `\n`;
    
    // Date-wise Breakdown Section
    csv += `DATE-WISE BREAKDOWN\n`;
    csv += `Date,Impressions,Clicks,CTR,Spend,Conversions\n`;
    trendData.forEach(day => {
      const dayCTR = day.impressions > 0 ? ((day.clicks / day.impressions) * 100).toFixed(2) : 0;
      csv += `${format(new Date(day.date), 'yyyy-MM-dd')},${day.impressions},${day.clicks},${dayCTR}%,$${(day.spend || 0).toFixed(2)},${day.conversions || 0}\n`;
    });
    csv += `\n`;
    
    // Date-wise Platform Breakdown
    if (trendData.length > 0 && platformData.length > 0) {
      csv += `DATE-WISE PLATFORM BREAKDOWN\n`;
      csv += `Date,Platform,Impressions,Clicks,CTR,Spend,Conversions\n`;
      
      const totalSpend = platformData.reduce((sum, p) => sum + (p.spend || 0), 0);
      const platformRatios = {};
      platformData.forEach(p => {
        platformRatios[p.platform] = totalSpend > 0 ? (p.spend || 0) / totalSpend : 0;
      });
      
      trendData.forEach(day => {
        platformData.forEach(p => {
          const ratio = platformRatios[p.platform] || 0;
          const dayImpressions = Math.round(day.impressions * ratio);
          const dayClicks = Math.round(day.clicks * ratio);
          const daySpend = (day.spend * ratio).toFixed(2);
          const dayConversions = Math.round((day.conversions || 0) * ratio);
          const dayCTR = dayImpressions > 0 ? ((dayClicks / dayImpressions) * 100).toFixed(2) : 0;
          csv += `${format(new Date(day.date), 'yyyy-MM-dd')},${p.platform},${dayImpressions},${dayClicks},${dayCTR}%,$${daySpend},${dayConversions}\n`;
        });
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Comprehensive_Report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Comprehensive report exported successfully');
  };

  // Export as PDF with Charts
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Report PRO - Performance Report', margin, yPosition);
      yPosition += 10;

      // Date range
      pdf.setFontSize(11);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Date Range: ${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`, margin, yPosition);
      yPosition += 6;
      pdf.text(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`, margin, yPosition);
      yPosition += 12;

      // Summary metrics
      const totalImpressions = platformData.reduce((sum, p) => sum + (p.impressions || 0), 0);
      const totalClicks = platformData.reduce((sum, p) => sum + (p.clicks || 0), 0);
      const totalSpend = platformData.reduce((sum, p) => sum + (p.spend || 0), 0);
      const totalConversions = platformData.reduce((sum, p) => sum + (p.conversions || 0), 0);
      const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;

      pdf.setFontSize(14);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Summary Metrics', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      const summaryData = [
        ['Total Impressions', formatNumber(totalImpressions)],
        ['Total Clicks', formatNumber(totalClicks)],
        ['Overall CTR', `${avgCTR}%`],
        ['Total Spend', `$${totalSpend.toFixed(2)}`],
        ['Total Conversions', formatNumber(totalConversions)],
      ];

      summaryData.forEach(([label, value]) => {
        pdf.setTextColor(100, 116, 139);
        pdf.text(label + ':', margin, yPosition);
        pdf.setTextColor(15, 23, 42);
        pdf.text(value, margin + 50, yPosition);
        yPosition += 5;
      });
      yPosition += 10;

      // Capture Pie Chart
      if (pieChartRef.current) {
        pdf.setFontSize(14);
        pdf.setTextColor(15, 23, 42);
        pdf.text('Platform Spend Distribution', margin, yPosition);
        yPosition += 5;

        const pieCanvas = await html2canvas(pieChartRef.current, { 
          scale: 2, 
          backgroundColor: '#ffffff',
          logging: false 
        });
        const pieImage = pieCanvas.toDataURL('image/png');
        const pieWidth = pageWidth - (margin * 2);
        const pieHeight = (pieCanvas.height / pieCanvas.width) * pieWidth;
        
        if (yPosition + pieHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.addImage(pieImage, 'PNG', margin, yPosition, pieWidth, Math.min(pieHeight, 80));
        yPosition += Math.min(pieHeight, 80) + 10;
      }

      // Capture Line Chart
      if (lineChartRef.current) {
        if (yPosition + 80 > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(15, 23, 42);
        pdf.text('Daily Performance Trend', margin, yPosition);
        yPosition += 5;

        const lineCanvas = await html2canvas(lineChartRef.current, { 
          scale: 2, 
          backgroundColor: '#ffffff',
          logging: false 
        });
        const lineImage = lineCanvas.toDataURL('image/png');
        const lineWidth = pageWidth - (margin * 2);
        const lineHeight = (lineCanvas.height / lineCanvas.width) * lineWidth;
        
        pdf.addImage(lineImage, 'PNG', margin, yPosition, lineWidth, Math.min(lineHeight, 80));
        yPosition += Math.min(lineHeight, 80) + 10;
      }

      // Platform breakdown table
      if (yPosition + 50 > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(14);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Platform Breakdown', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(8);
      const headers = ['Platform', 'Impressions', 'Clicks', 'CTR', 'Spend', 'CPC', 'Conversions'];
      const colWidths = [35, 28, 22, 18, 25, 22, 28];
      let xPos = margin;

      pdf.setFillColor(241, 245, 249);
      pdf.rect(margin, yPosition - 4, pageWidth - (margin * 2), 7, 'F');
      pdf.setTextColor(100, 116, 139);
      headers.forEach((header, i) => {
        pdf.text(header, xPos, yPosition);
        xPos += colWidths[i];
      });
      yPosition += 6;

      pdf.setTextColor(15, 23, 42);
      platformData.forEach(p => {
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        xPos = margin;
        const row = [
          p.platform,
          formatNumber(p.impressions),
          formatNumber(p.clicks),
          `${p.ctr}%`,
          `$${(p.spend || 0).toFixed(0)}`,
          `$${(p.cpc || 0).toFixed(2)}`,
          formatNumber(p.conversions)
        ];
        row.forEach((cell, i) => {
          pdf.text(String(cell), xPos, yPosition);
          xPos += colWidths[i];
        });
        yPosition += 5;
      });

      // Campaign Breakdown Table
      yPosition += 10;
      if (yPosition + 50 > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(14);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Campaign Breakdown', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(7);
      const campaignHeaders = ['Campaign', 'Platform', 'Impressions', 'Clicks', 'CTR', 'Spend', 'CPC'];
      const campaignColWidths = [55, 25, 25, 22, 16, 22, 18];
      xPos = margin;

      pdf.setFillColor(241, 245, 249);
      pdf.rect(margin, yPosition - 4, pageWidth - (margin * 2), 7, 'F');
      pdf.setTextColor(100, 116, 139);
      campaignHeaders.forEach((header, i) => {
        pdf.text(header, xPos, yPosition);
        xPos += campaignColWidths[i];
      });
      yPosition += 6;

      pdf.setTextColor(15, 23, 42);
      campaignData.slice(0, 20).forEach(c => {
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        xPos = margin;
        const campaignName = (c.campaign_name || 'Unknown').substring(0, 30);
        const row = [
          campaignName,
          c.platform || '',
          formatNumber(c.impressions),
          formatNumber(c.clicks),
          `${c.ctr}%`,
          `$${(c.spend || 0).toFixed(0)}`,
          `$${(c.cpc || 0).toFixed(2)}`
        ];
        row.forEach((cell, i) => {
          pdf.text(String(cell), xPos, yPosition);
          xPos += campaignColWidths[i];
        });
        yPosition += 5;
      });

      if (campaignData.length > 20) {
        pdf.setTextColor(100, 116, 139);
        pdf.text(`... and ${campaignData.length - 20} more campaigns`, margin, yPosition);
        yPosition += 5;
      }

      // Date-wise Breakdown Table
      yPosition += 10;
      if (yPosition + 50 > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(14);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Date-wise Breakdown', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(8);
      const dateHeaders = ['Date', 'Impressions', 'Clicks', 'CTR', 'Spend', 'Conversions'];
      const dateColWidths = [30, 30, 25, 20, 30, 28];
      xPos = margin;

      pdf.setFillColor(241, 245, 249);
      pdf.rect(margin, yPosition - 4, pageWidth - (margin * 2), 7, 'F');
      pdf.setTextColor(100, 116, 139);
      dateHeaders.forEach((header, i) => {
        pdf.text(header, xPos, yPosition);
        xPos += dateColWidths[i];
      });
      yPosition += 6;

      pdf.setTextColor(15, 23, 42);
      trendData.slice(0, 30).forEach(day => {
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        xPos = margin;
        const dayCTR = day.impressions > 0 ? ((day.clicks / day.impressions) * 100).toFixed(2) : 0;
        const row = [
          format(new Date(day.date), 'MMM dd, yyyy'),
          formatNumber(day.impressions),
          formatNumber(day.clicks),
          `${dayCTR}%`,
          `$${(day.spend || 0).toFixed(2)}`,
          formatNumber(day.conversions || 0)
        ];
        row.forEach((cell, i) => {
          pdf.text(String(cell), xPos, yPosition);
          xPos += dateColWidths[i];
        });
        yPosition += 5;
      });

      if (trendData.length > 30) {
        pdf.setTextColor(100, 116, 139);
        pdf.text(`... and ${trendData.length - 30} more days`, margin, yPosition);
      }

      pdf.save(`Report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF report with charts exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Export as Excel with Charts
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const workbook = XLSX.utils.book_new();

      // Summary Sheet
      const summaryData = [
        ['Report PRO - Performance Report'],
        [`Date Range: ${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`],
        [`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`],
        [],
        ['Summary Metrics'],
        ['Metric', 'Value'],
        ['Total Impressions', platformData.reduce((sum, p) => sum + (p.impressions || 0), 0)],
        ['Total Clicks', platformData.reduce((sum, p) => sum + (p.clicks || 0), 0)],
        ['Total Spend', platformData.reduce((sum, p) => sum + (p.spend || 0), 0)],
        ['Total Conversions', platformData.reduce((sum, p) => sum + (p.conversions || 0), 0)],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // Platform Breakdown Sheet
      const platformSheetData = [
        ['Platform Breakdown'],
        ['Platform', 'Impressions', 'Clicks', 'CTR (%)', 'Spend ($)', 'CPC ($)', 'CPM ($)', 'Conversions'],
        ...platformData.map(p => [
          p.platform,
          p.impressions,
          p.clicks,
          p.ctr,
          p.spend || 0,
          p.cpc || 0,
          p.cpm || 0,
          p.conversions || 0
        ])
      ];
      const platformSheet = XLSX.utils.aoa_to_sheet(platformSheetData);
      XLSX.utils.book_append_sheet(workbook, platformSheet, 'Platform Breakdown');

      // Campaign Breakdown Sheet
      const campaignSheetData = [
        ['Campaign Breakdown'],
        ['Campaign', 'Platform', 'Ad Account', 'Impressions', 'Clicks', 'CTR (%)', 'Spend ($)', 'CPC ($)', 'CPM ($)', 'Conversions'],
        ...campaignData.map(c => [
          c.campaign_name || 'Unknown',
          c.platform || '',
          c.ad_account_name || '',
          c.impressions,
          c.clicks,
          c.ctr,
          c.spend || 0,
          c.cpc || 0,
          c.cpm || 0,
          c.conversions || 0
        ])
      ];
      const campaignSheet = XLSX.utils.aoa_to_sheet(campaignSheetData);
      XLSX.utils.book_append_sheet(workbook, campaignSheet, 'Campaign Breakdown');

      // Date-wise Sheet
      const dateSheetData = [
        ['Date-wise Breakdown'],
        ['Date', 'Impressions', 'Clicks', 'CTR (%)', 'Spend ($)', 'Conversions'],
        ...trendData.map(day => [
          format(new Date(day.date), 'yyyy-MM-dd'),
          day.impressions,
          day.clicks,
          day.impressions > 0 ? ((day.clicks / day.impressions) * 100).toFixed(2) : 0,
          day.spend || 0,
          day.conversions || 0
        ])
      ];
      const dateSheet = XLSX.utils.aoa_to_sheet(dateSheetData);
      XLSX.utils.book_append_sheet(workbook, dateSheet, 'Date-wise Breakdown');

      // Capture charts as images and add to a new sheet
      if (pieChartRef.current || lineChartRef.current) {
        const chartImages = [];
        
        if (pieChartRef.current) {
          const pieCanvas = await html2canvas(pieChartRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false });
          chartImages.push({ name: 'Pie Chart', dataUrl: pieCanvas.toDataURL('image/png') });
        }
        
        if (lineChartRef.current) {
          const lineCanvas = await html2canvas(lineChartRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false });
          chartImages.push({ name: 'Trend Chart', dataUrl: lineCanvas.toDataURL('image/png') });
        }

        // Save chart images separately
        chartImages.forEach((chart, index) => {
          const link = document.createElement('a');
          link.href = chart.dataUrl;
          link.download = `Chart_${index + 1}_${format(dateRange.from, 'yyyy-MM-dd')}.png`;
          link.click();
        });
      }

      // Write Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `Report_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.xlsx`);
      
      toast.success('Excel report exported (charts saved as separate images)');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Reports</h1>
            <p className="text-sm text-slate-500 mt-1">Detailed platform performance reports</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Export Platform Report */}
            <Button 
              variant="outline" 
              onClick={handleExportPlatformCSV} 
              disabled={loading || platformData.length === 0} 
              data-testid="export-platform-report-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Platform Report
            </Button>
            
            {/* Export Comprehensive Report (includes all breakdowns) */}
            <Button 
              variant="outline" 
              onClick={handleExportDatewiseCSV} 
              disabled={loading || trendData.length === 0} 
              data-testid="export-datewise-report-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Full Report (CSV)
            </Button>

            {/* Export with Charts Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="default" 
                  disabled={loading || exporting || platformData.length === 0}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="export-charts-dropdown"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Image className="w-4 h-4 mr-2" />
                  )}
                  Export with Charts
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Multi-select Platform Filter */}
              <div className="relative">
                <Popover open={platformDropdownOpen} onOpenChange={setPlatformDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-[220px] justify-between"
                      data-testid="report-platform-filter"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Filter className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {selectedPlatforms.length === 0 
                            ? 'All Platforms' 
                            : `${selectedPlatforms.length} Platform${selectedPlatforms.length > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[220px] p-2" align="start">
                    <div className="flex justify-between items-center pb-2 border-b mb-2">
                      <span className="text-sm font-medium">Platforms</span>
                      {selectedPlatforms.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearPlatformFilter} className="h-6 px-2 text-xs">
                          Clear
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Search platforms..."
                      className="mb-2 h-8"
                      value={platformSearch}
                      onChange={(e) => setPlatformSearch(e.target.value)}
                    />
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {platforms
                        .filter(p => p.toLowerCase().includes(platformSearch.toLowerCase()))
                        .map((platform) => (
                        <label
                          key={platform}
                          className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedPlatforms.includes(platform)}
                            onCheckedChange={() => togglePlatform(platform)}
                          />
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPlatformColor(platform) }} />
                          <span className="text-sm">{platform}</span>
                        </label>
                      ))}
                      {platforms.filter(p => p.toLowerCase().includes(platformSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs text-slate-500 text-center py-4">No platforms match your search</p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Multi-select Ad Account Filter - Filtered by selected platforms */}
              <div className="relative">
                <Popover open={adAccountDropdownOpen} onOpenChange={setAdAccountDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-[200px] justify-between"
                      data-testid="report-ad-account-filter"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Building2 className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {selectedAdAccounts.length === 0 
                            ? 'All Ad Accounts' 
                            : `${selectedAdAccounts.length} Account${selectedAdAccounts.length > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[260px] p-2" align="start">
                    <div className="flex justify-between items-center pb-2 border-b mb-2">
                      <span className="text-sm font-medium">Ad Accounts</span>
                      {selectedAdAccounts.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearAdAccountFilter} className="h-6 px-2 text-xs">
                          Clear
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Search ad accounts..."
                      className="mb-2 h-8"
                      value={adAccountSearch}
                      onChange={(e) => setAdAccountSearch(e.target.value)}
                    />
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {adAccounts.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-4">
                          {selectedPlatforms.length > 0 
                            ? 'No ad accounts for selected platforms'
                            : 'No ad accounts available'}
                        </p>
                      ) : (
                        adAccounts
                          .filter(acc => acc.name?.toLowerCase().includes(adAccountSearch.toLowerCase()))
                          .map((acc) => (
                          <label
                            key={acc.id}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedAdAccounts.includes(acc.id)}
                              onCheckedChange={() => toggleAdAccount(acc.id)}
                            />
                            <Building2 className="w-3 h-3 text-slate-400" />
                            <span className="text-sm truncate" title={acc.name}>{acc.name}</span>
                          </label>
                        ))
                      )}
                      {adAccounts.length > 0 && adAccounts.filter(acc => acc.name?.toLowerCase().includes(adAccountSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs text-slate-500 text-center py-4">No ad accounts match your search</p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Multi-select Campaign Filter - Uses filteredCampaigns based on platform and ad account selection */}
              <div className="relative">
                <Popover open={campaignDropdownOpen} onOpenChange={setCampaignDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-[220px] justify-between"
                      data-testid="report-campaign-filter"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Filter className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {selectedCampaigns.length === 0 
                            ? 'All Campaigns' 
                            : `${selectedCampaigns.length} Campaign${selectedCampaigns.length > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-2" align="start">
                    <div className="flex justify-between items-center pb-2 border-b mb-2">
                      <span className="text-sm font-medium">Campaigns</span>
                      {selectedCampaigns.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearCampaignFilter} className="h-6 px-2 text-xs">
                          Clear
                        </Button>
                      )}
                    </div>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {/* Search input */}
                      <Input
                        placeholder="Search campaigns..."
                        className="mb-2 h-8"
                        onChange={(e) => setCampaignSearch?.(e.target.value)}
                      />
                      {filteredCampaigns.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-4">
                          {selectedPlatforms.length > 0 || selectedAdAccounts.length > 0 
                            ? 'No campaigns match the selected filters'
                            : 'No campaigns available'}
                        </p>
                      ) : (
                        <>
                          {filteredCampaigns
                            .filter(c => c.name?.toLowerCase().includes((campaignSearch || '').toLowerCase()))
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .slice(0, 50) // Limit to 50 for performance
                            .map((campaign) => (
                            <label
                              key={campaign.id}
                              className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedCampaigns.includes(campaign.id)}
                                onCheckedChange={() => toggleCampaign(campaign.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm block truncate" title={campaign.name}>{campaign.name}</span>
                                <div className="flex items-center gap-1 mt-0.5">
                                  {campaign.ad_account_name && (
                                    <span className="text-xs text-slate-500 truncate">{campaign.ad_account_name}</span>
                                  )}
                                </div>
                              </div>
                            </label>
                          ))}
                          {filteredCampaigns.length > 50 && (
                            <p className="text-xs text-slate-400 text-center py-2">
                              Showing 50 of {filteredCampaigns.length} campaigns. Use search to find more.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date Range */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="min-w-[240px] justify-start text-left" data-testid="report-date-range-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {dateRange.from && dateRange.to && format(dateRange.from, 'yyyy-MM-dd') === format(dateRange.to, 'yyyy-MM-dd')
                      ? format(dateRange.from, 'MMM d, yyyy')
                      : `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="flex">
                    {/* Presets sidebar */}
                    <div className="border-r border-slate-200 p-3 space-y-1">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Quick Select</p>
                      {datePresets.map((preset) => (
                        <button
                          key={preset.key}
                          onClick={() => applyDatePreset(preset)}
                          data-testid={`date-preset-${preset.key}`}
                          className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                            activePreset === preset.key
                              ? 'bg-slate-900 text-white'
                              : 'hover:bg-slate-100 text-slate-700'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    {/* Calendar */}
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={handleCustomDateChange}
                      numberOfMonths={2}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Range Preset Pills (visible below filters) */}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <span className="text-xs text-slate-500 self-center mr-2">Quick filters:</span>
              {datePresets.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => applyDatePreset(preset)}
                  data-testid={`date-pill-${preset.key}`}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    activePreset === preset.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Active Filters Display */}
            {(selectedPlatforms.length > 0 || selectedCampaigns.length > 0 || selectedAdAccounts.length > 0) && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                {selectedPlatforms.map((platform) => (
                  <span
                    key={platform}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-full text-xs font-medium"
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPlatformColor(platform) }} />
                    {platform}
                    <button onClick={() => togglePlatform(platform)} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedAdAccounts.map((accountId) => {
                  const account = adAccounts.find(a => a.id === accountId);
                  return account ? (
                    <span
                      key={accountId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium"
                    >
                      <Building2 className="w-3 h-3" />
                      {account.name}
                      <button onClick={() => toggleAdAccount(accountId)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null;
                })}
                {selectedCampaigns.map((campaignId) => {
                  const campaign = campaigns.find(c => c.id === campaignId);
                  return campaign ? (
                    <span
                      key={campaignId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                    >
                      {campaign.name}
                      <button onClick={() => toggleCampaign(campaignId)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${canViewSourceDomain ? 'grid-cols-2' : 'grid-cols-1'} lg:w-[400px]`}>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            {canViewSourceDomain && (
              <TabsTrigger value="dv360-breakdown" data-testid="tab-dv360">DV360 Source & Domain</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Platform Spend Distribution - Pie Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Platform Spend Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]" ref={pieChartRef}>
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : platformData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={platformData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ platform, percent }) => `${platform} (${(percent * 100).toFixed(2)}%)`}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="spend"
                        nameKey="platform"
                        paddingAngle={2}
                      >
                        {platformData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={CHART_COLORS[index % CHART_COLORS.length]} 
                            stroke="#fff"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Legend 
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No data available for the selected filters
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Daily Trend Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Daily Performance Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]" ref={lineChartRef}>
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#64748B' }}
                        tickFormatter={(value) => format(new Date(value), 'MMM d')}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 11, fill: '#64748B' }}
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 11, fill: '#64748B' }}
                        tickFormatter={(value) => `$${value.toFixed(0)}`}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === 'spend') return formatCurrency(value);
                          return formatNumber(value);
                        }}
                        labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                      />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="#0284C7" strokeWidth={2} dot={false} name="Impressions" />
                      <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#059669" strokeWidth={2} dot={false} name="Clicks" />
                      <Line yAxisId="right" type="monotone" dataKey="spend" stroke="#D97706" strokeWidth={2} dot={false} name="Spend" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No data available for the selected filters
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Platform Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Platform Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platform</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CTR</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPC</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPM</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {Array.from({ length: 8 }).map((_, j) => (
                          <td key={j} className="py-4 px-4">
                            <div className="h-4 bg-slate-200 animate-pulse rounded" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : platformData.length > 0 ? (
                    platformData.map((platform, index) => (
                      <tr key={platform.platform} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                            />
                            <span className="font-medium text-slate-900">{platform.platform}</span>
                          </div>
                        </td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatNumber(platform.impressions)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatNumber(platform.clicks)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatPercentage(platform.ctr)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatCurrency(platform.spend)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatCurrency(platform.cpc)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatCurrency(platform.cpm)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatNumber(platform.conversions)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400">
                        No data available for the selected filters
                      </td>
                    </tr>
                  )}
                </tbody>
                {platformData.length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-50 font-medium">
                      <td className="py-3 px-4 text-slate-900">Total</td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatNumber(platformData.reduce((sum, p) => sum + p.impressions, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatNumber(platformData.reduce((sum, p) => sum + p.clicks, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">—</td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatCurrency(platformData.reduce((sum, p) => sum + p.spend, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">—</td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">—</td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatNumber(platformData.reduce((sum, p) => sum + p.conversions, 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Campaign Breakdown Table */}
        <Card className="border-slate-200" data-testid="campaign-breakdown-section">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Campaign Breakdown
            </CardTitle>
            <span className="text-xs text-slate-500">
              {campaignPagination.total_count} campaigns
            </span>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Campaign</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platform</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ad Account</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CTR</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPC</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPM</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j} className="py-4 px-4">
                            <div className="h-4 bg-slate-200 animate-pulse rounded" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : campaignData.length > 0 ? (
                    campaignData.map((campaign, index) => (
                      <tr key={`${campaign.campaign_id}-${index}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-4">
                          <span className="font-medium text-slate-900 block max-w-[200px] truncate" title={campaign.campaign_name}>
                            {campaign.campaign_name}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: getPlatformColor(campaign.platform) }}
                            />
                            <span className="text-slate-700">{campaign.platform}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-600 text-sm max-w-[150px] truncate block" title={campaign.ad_account_name}>
                            {campaign.ad_account_name || '—'}
                          </span>
                        </td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatNumber(campaign.impressions)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatNumber(campaign.clicks)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatPercentage(campaign.ctr)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatCurrency(campaign.spend)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatCurrency(campaign.cpc)}</td>
                        <td className="text-right py-4 px-4 font-mono text-slate-700">{formatCurrency(campaign.cpm)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-400">
                        No campaign data available for the selected filters
                      </td>
                    </tr>
                  )}
                </tbody>
                {campaignData.length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-50 font-medium">
                      <td className="py-3 px-4 text-slate-900">Total ({campaignData.length} shown)</td>
                      <td className="py-3 px-4"></td>
                      <td className="py-3 px-4"></td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatNumber(campaignData.reduce((sum, c) => sum + c.impressions, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatNumber(campaignData.reduce((sum, c) => sum + c.clicks, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">—</td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">
                        {formatCurrency(campaignData.reduce((sum, c) => sum + c.spend, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">—</td>
                      <td className="text-right py-3 px-4 font-mono text-slate-900">—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            
            {/* Pagination */}
            {campaignPagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-slate-500">
                  Page {campaignPagination.page} of {campaignPagination.total_pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCampaignPage(1)}
                    disabled={campaignPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCampaignPage(p => Math.max(1, p - 1))}
                    disabled={campaignPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCampaignPage(p => Math.min(campaignPagination.total_pages, p + 1))}
                    disabled={campaignPage >= campaignPagination.total_pages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCampaignPage(campaignPagination.total_pages)}
                    disabled={campaignPage >= campaignPagination.total_pages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Date-wise Platform Breakdown Table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Date-wise Platform Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full data-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platform</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CTR</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Conversions</th>
                  </tr>
                </thead>
                <tbody>
                  {trendData.length > 0 ? (
                    trendData.flatMap((dayData) => {
                      // Group by platform for each day
                      const platforms = [...new Set(platformData.map(p => p.platform))];
                      return platforms.map((platform, idx) => {
                        const platformMetrics = platformData.find(p => p.platform === platform) || {};
                        // Estimate daily values based on proportion
                        const totalSpend = platformData.reduce((sum, p) => sum + p.spend, 0);
                        const ratio = totalSpend > 0 ? (platformMetrics.spend || 0) / totalSpend : 0;
                        return (
                          <tr key={`${dayData.date}-${platform}`} className="border-b border-slate-100 hover:bg-slate-50">
                            {idx === 0 && (
                              <td className="py-2 px-4 font-medium text-slate-700" rowSpan={platforms.length}>
                                {format(new Date(dayData.date), 'MMM d, yyyy')}
                              </td>
                            )}
                            <td className="py-2 px-4">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPlatformColor(platform) }} />
                                {platform}
                              </span>
                            </td>
                            <td className="text-right py-2 px-4 font-mono text-sm">
                              {formatNumber(Math.round(dayData.impressions * ratio))}
                            </td>
                            <td className="text-right py-2 px-4 font-mono text-sm">
                              {formatNumber(Math.round(dayData.clicks * ratio))}
                            </td>
                            <td className="text-right py-2 px-4 font-mono text-sm">
                              {(dayData.impressions * ratio > 0 
                                ? ((dayData.clicks * ratio) / (dayData.impressions * ratio) * 100).toFixed(2) 
                                : 0)}%
                            </td>
                            <td className="text-right py-2 px-4 font-mono text-sm">
                              {formatCurrency(dayData.spend * ratio)}
                            </td>
                            <td className="text-right py-2 px-4 font-mono text-sm">
                              {formatNumber(Math.round((dayData.conversions || 0) * ratio))}
                            </td>
                          </tr>
                        );
                      });
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400">
                        No date-wise data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* DV360 Source & Domain Tab */}
          <TabsContent value="dv360-breakdown" className="mt-6">
            <div className="space-y-6">
              {dv360Loading ? (
                <Card className="border-slate-200">
                  <CardContent className="py-12">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                      <p className="text-slate-500">Fetching DV360 breakdown data...</p>
                      <p className="text-xs text-slate-400">This may take a few moments as we query the DBM API</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Source (Exchange) Breakdown */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        Source (Exchange) Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full data-table">
                          <thead>
                            <tr className="border-b border-slate-200 bg-slate-50">
                              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source/Exchange</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CTR</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPM</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dv360Report.sources.length > 0 ? (
                              dv360Report.sources.map((source, index) => (
                                <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                  <td className="py-3 px-4 font-medium text-slate-900">{source.name}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatNumber(source.impressions)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatNumber(source.clicks)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatPercentage(source.ctr)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-900 font-medium">{formatCurrency(source.spend)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatCurrency(source.cpm)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="py-12 text-center text-slate-400">
                                  No source data available. Click "DV360 Source & Domain" tab to fetch data.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Domain (Site) Breakdown */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        App/Site ID Breakdown - Top 100
                        <span className="text-xs font-normal text-slate-400 ml-2">(DV360 uses IDs for privacy)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                        <table className="w-full data-table">
                          <thead className="sticky top-0 bg-slate-50">
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">App/Site ID</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Impressions</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Clicks</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CTR</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                              <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPM</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dv360Report.domains.length > 0 ? (
                              dv360Report.domains.map((domain, index) => (
                                <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                  <td className="py-3 px-4 font-medium text-slate-900 max-w-[300px] truncate" title={domain.name}>{domain.name}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatNumber(domain.impressions)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatNumber(domain.clicks)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatPercentage(domain.ctr)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-900 font-medium">{formatCurrency(domain.spend)}</td>
                                  <td className="text-right py-3 px-4 font-mono text-slate-700">{formatCurrency(domain.cpm)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="py-12 text-center text-slate-400">
                                  No domain data available. Click "DV360 Source & Domain" tab to fetch data.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
